import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Download, CreditCard, CheckCircle, Clock, AlertCircle, Receipt,
  Loader2, Shield, Calendar
} from 'lucide-react';
import { API } from '@/App';

const MesAdhesionsPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    axios.get(`${API}/my-orders`)
      .then(r => setOrders(r.data.orders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDownloadPdf = async (orderId, type) => {
    setDownloadingId(`${orderId}-${type}`);
    try {
      const endpoint = type === 'receipt' ? 'receipt' : 'justificatif';
      const res = await axios.get(`${API}/my-orders/${orderId}/${endpoint}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type === 'receipt' ? 'attestation' : 'justificatif'}_${orderId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document téléchargé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du téléchargement');
    }
    setDownloadingId(null);
  };

  const statusConfig = {
    completed: { label: 'Validée', icon: CheckCircle, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    pending: { label: 'En attente', icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    cancelled: { label: 'Annulée', icon: AlertCircle, cls: 'bg-red-50 text-red-700 border-red-200' },
  };

  return (
    <div className="min-h-full bg-[#f8fafb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="mes-adhesions-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Mes Adhésions & Reçus
          </h1>
          <p className="text-sm text-gray-500 mt-1">Historique de vos cotisations et documents fiscaux</p>
        </div>

        {/* Subscription Status Banner */}
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 border ${
          user?.subscription_status === 'active'
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-amber-50 border-amber-200'
        }`} data-testid="subscription-status">
          <Shield size={24} className={user?.subscription_status === 'active' ? 'text-emerald-600' : 'text-amber-600'} />
          <div>
            <p className={`text-sm font-bold ${user?.subscription_status === 'active' ? 'text-emerald-800' : 'text-amber-800'}`}>
              Adhésion {user?.subscription_status === 'active' ? 'active' : 'inactive'}
            </p>
            <p className={`text-xs ${user?.subscription_status === 'active' ? 'text-emerald-700' : 'text-amber-700'}`}>
              {user?.subscription_status === 'active'
                ? 'Votre adhésion est à jour. Vos fiches sont visibles dans l\'annuaire.'
                : 'Votre adhésion n\'est pas active. Renouvelez-la pour accéder à tous les avantages.'}
            </p>
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 size={32} className="animate-spin text-[#35AFE4]" /></div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <CreditCard size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 mb-3">Aucune adhésion trouvée</p>
            <a href="/adhesion" className="text-sm text-[#35AFE4] font-semibold hover:underline" data-testid="go-adhesion-btn">
              Adhérer à la SFOALC
            </a>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((order, i) => {
              const sc = statusConfig[order.status] || statusConfig.pending;
              const StatusIcon = sc.icon;
              const canDownload = order.status === 'completed' && order.amount > 0;
              return (
                <div key={order.id} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm" data-testid={`order-card-${i}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-bold text-[#1e3a5f]">
                          {order.product || 'Adhésion SFOALC'}
                        </h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${sc.cls}`}>
                          <StatusIcon size={10} /> {sc.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {order.order_date ? new Date(order.order_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                        </span>
                        <span className="font-mono font-bold">#{order.wc_order_number}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#1e3a5f]">{order.amount?.toFixed(2)} €</p>
                    </div>
                  </div>

                  {canDownload && (
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                      <Receipt size={14} className="text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium mr-auto">Documents disponibles :</span>
                      <button onClick={() => handleDownloadPdf(order.id, 'receipt')} disabled={downloadingId === `${order.id}-receipt`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-xs font-semibold hover:bg-[#162d4a] transition-colors disabled:opacity-50"
                        data-testid={`download-receipt-${i}`}>
                        {downloadingId === `${order.id}-receipt` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                        Attestation fiscale
                      </button>
                      <button onClick={() => handleDownloadPdf(order.id, 'justificatif')} disabled={downloadingId === `${order.id}-justificatif`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#35AFE4] text-white text-xs font-semibold hover:bg-[#2d7fa5] transition-colors disabled:opacity-50"
                        data-testid={`download-justificatif-${i}`}>
                        {downloadingId === `${order.id}-justificatif` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                        Justificatif
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MesAdhesionsPage;
